import { EFinancingProgram } from '@if/core/types'
import { type FormSetup } from '@if/mui'
import { FinancingConfigDto } from '@src/data/api/financing-config-api'
import { useUpdateMerchant, type UpdateMerchantDto } from '@src/data/api/merchants-api/merchants-api'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantFinancingProgramConfig } from '@src/data/types/MerchantFinancingProgramConfig'
import { useCallback, useMemo } from 'react'
import { buildUpdateMerchantDto } from '../buildUpdateMerchantDto'
import MerchantFinancingProgramConfigsPanel, {
  makeMerchantFinancingProgramConfigsSchema,
  type MerchantFinancingProgramConfigsSchemaType,
} from '../MerchantFinancingProgramConfigsPanel'
import { useSyncFormDefaults } from '../useSyncFormDefaults'
import { MerchantTabConfig, TabFormMethods } from './MerchantGeneralTab'

type ProgramConfigsSchemaType = ReturnType<typeof makeMerchantFinancingProgramConfigsSchema>

function getProgramConfigsDefaults(merchant: Merchant): MerchantFinancingProgramConfigsSchemaType {
  const financingProgramConfigs = Object.values(EFinancingProgram).reduce(
    (acc, program) => {
      const config = merchant.financingProgramConfigs?.find((c) => c.financingProgramId == program)
      if (config) acc[program] = config
      return acc
    },
    {} as Partial<Record<EFinancingProgram, MerchantFinancingProgramConfig>>,
  )

  return { financingProgramConfigs } as MerchantFinancingProgramConfigsSchemaType
}

function toProgramConfigsOverride(values: MerchantFinancingProgramConfigsSchemaType): Partial<UpdateMerchantDto> {
  const financingProgramConfigs = Object.entries(values.financingProgramConfigs)
    .filter(([, config]) => config && Object.keys(config).length !== 0)
    .map(([program, config]) => ({
      financingProgramId: program as EFinancingProgram,
      ...config,
    })) as MerchantFinancingProgramConfig[]

  return { financingProgramConfigs }
}

interface MerchantProgramConfigsTabContentProps {
  merchant: Merchant
  financingConfigs: FinancingConfigDto[]
  serviceCategory: string | undefined
  defaults: MerchantFinancingProgramConfigsSchemaType
}

const MerchantProgramConfigsTabContent = ({
  merchant,
  financingConfigs,
  serviceCategory,
  defaults,
}: MerchantProgramConfigsTabContentProps) => {
  useSyncFormDefaults<MerchantFinancingProgramConfigsSchemaType>(defaults)

  return (
    <MerchantFinancingProgramConfigsPanel
      supportedFinancingPrograms={merchant.supportedFinancingPrograms}
      financingConfigs={financingConfigs}
      serviceCategory={serviceCategory}
    />
  )
}

/**
 * Builds the Financing Programs tab's form config (financing-program configs
 * slice only) and body.
 */
export function useMerchantProgramConfigsTab(
  merchant: Merchant,
  financingConfigs: FinancingConfigDto[],
): MerchantTabConfig<ProgramConfigsSchemaType> {
  const [updateMerchant] = useUpdateMerchant()

  const serviceCategory = merchant.serviceCategory ?? undefined
  const schema = useMemo(
    () => makeMerchantFinancingProgramConfigsSchema(financingConfigs, serviceCategory),
    [financingConfigs, serviceCategory],
  )
  const defaults = useMemo(() => getProgramConfigsDefaults(merchant), [merchant])

  const useFormSetup = useCallback(
    (): FormSetup<ProgramConfigsSchemaType> => ({
      schema,
      defaultValues: defaults,
    }),
    [schema, defaults],
  )

  const onSubmit = useCallback(
    async (values: MerchantFinancingProgramConfigsSchemaType, methods: TabFormMethods<ProgramConfigsSchemaType>) => {
      await updateMerchant(buildUpdateMerchantDto(merchant, toProgramConfigsOverride(values)))
      methods.reset(values)
    },
    [merchant, updateMerchant],
  )

  const children = useCallback(
    () => (
      <MerchantProgramConfigsTabContent
        merchant={merchant}
        financingConfigs={financingConfigs}
        serviceCategory={serviceCategory}
        defaults={defaults}
      />
    ),
    [merchant, financingConfigs, serviceCategory, defaults],
  )

  return { useFormSetup, onSubmit, children }
}
